import CheckIcon from '@mui/icons-material/Check';
import { Box, ListItemButton, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { SingleSelectOption } from './types';

export const ListOptions = ({
  isOpen,
  activeIndex,
  handleSelectOption,
  options,
  selectedOption,
}: {
  isOpen: boolean;
  activeIndex: number;
  handleSelectOption: (value: string) => void;
  options: SingleSelectOption[];
  selectedOption?: string;
}) => {
  const listOptionsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const setItemListItemRef =
    (listItemIndex: number): React.RefCallback<HTMLDivElement> =>
    listItemRef => {
      listOptionsRefs.current[listItemIndex] = listItemRef;
    };

  useEffect(() => {
    if (isOpen) {
      listOptionsRefs.current[activeIndex]?.focus({ preventScroll: true });
    }
  }, [isOpen, activeIndex]);

  return (
    <>
      {options.map(({ label: optionLabel, value, count }, idx) => {
        const selected = selectedOption === value;
        return (
          <ListItemButton
            key={value}
            id={`option-${value}`}
            ref={setItemListItemRef(idx)}
            role="option"
            aria-selected={selected}
            tabIndex={idx === activeIndex ? 0 : -1}
            onClick={() => handleSelectOption(value)}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              height: 36,
              width: '100%',
              bgcolor: selected ? 'white' : 'transparent',
              '&:hover': {
                bgcolor: 'neutral.lightest',
              },
              '&:focus-visible, &.Mui-focusVisible': {
                outline: '2px solid #4B99E6',
                outlineOffset: '-2px',
                backgroundColor: 'transparent',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: 'text.secondary', fontWeight: 400, fontSize: 14 }}>
                {optionLabel}
              </Typography>
              {count !== undefined && (
                <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>({count})</Typography>
              )}
            </Box>
            <Box sx={{ width: 24, display: 'flex', justifyContent: 'flex-end' }}>
              {selected && <CheckIcon fontSize="small" sx={{ color: 'greens.main' }} />}
            </Box>
          </ListItemButton>
        );
      })}
    </>
  );
};
